import { ButtonWidget } from '@tableau/widgets';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as _ from 'underscore';

import { Messages } from '../../codegen/Localize';
import { PageHeaderButtonClickEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { ContentLocation, FavoriteToggleType } from '../../common/telemetryEvents/types/ContentTelemetryTypes';
import { generateTelemetryEventHandler } from '../../common/telemetryEvents/util/ContentTelemetry';
import { Telemetry } from '../../commonInfra/services/Telemetry';
import { IDataSourceDetailInfo } from '../../datasource/models/IDataSourceInfo';
import { IMetricInfo } from '../../metrics/models/IMetricInfo';
import { IContentInfo } from '../../ts/models/IContentInfo';
import { IViewDetailInfo } from '../../ts/models/IViewInfo';
import { ConnectedBreadcrumbs } from '../content/breadcrumbs/ConnectedBreadcrumbs';
import { FavoriteToggleClickedChildFunction, FavoriteToggleClickedTelemetryContext } from '../contexts/TelemetryContexts';
import { PreviousUrlInitialState } from '../reducers/PreviousUrlReducer';
import { ServerUrls } from '../ServerUrls';
import { isMetricDetailSpace, isMetricSpace, isUsageAnalyticsSubspace, SpaceInfo } from '../state/SpaceInfo';
import { BackButton } from './BackButton';
import { ConnectedPageHeaderMenus } from './ConnectedPageHeaderMenus';
import { LeftItem, PageHeaderProps, pageHeaderSearchActiveStyle, Region, RightItem } from './PageHeader';
import { PageHeaderSearch, VizViewerSearchButton } from './PageHeaderSearch';
import { PageHeaderStyles } from './PageHeaderStyles';
import { isValidSpaceInfoForVizView, VizViewerFavoriteButton, VizViewerPageHeaderButtons } from './VizViewerPageHeaderButtons';

const vizHeaderSearchOpenTestId = 'viz-header-search-open';
export const vizHeaderSearchOpenButtonTestId = vizHeaderSearchOpenTestId + '-Button';
const vizHeaderSearchCloseTestId = 'viz-header-search-close';
export const vizHeaderSearchCloseButtonTestId = vizHeaderSearchCloseTestId + '-Button';

interface VizViewerPageHeaderState {
  readonly headerSearchStyle: string;
  readonly isSearchOpen: boolean;
  readonly vizViewerSearchButtonRef: HTMLElement | undefined;
}

export class VizViewerPageHeader extends React.PureComponent<PageHeaderProps, VizViewerPageHeaderState> {
  constructor(props: PageHeaderProps) {
    super(props);
    this.state = { isSearchOpen: false, vizViewerSearchButtonRef: undefined, headerSearchStyle: '' };
  }

  public render() {
    const parentId = isUsageAnalyticsSubspace(this.props.spaceInfo) ? this.getParentDatasourceID() :
      isMetricDetailSpace(this.props.spaceInfo) ? this.getMetricParentProjectId() :
        this.getVizParentWorkbookId();
    return (
      // TFSID 943709: We need inline styling to format the header in a background tab in order to serve the correct DSD layout.
      <div style={PageHeaderStyles.ContainerVizViewerStyles}>
        <span className={PageHeaderStyles.LeftRegionVizViewer} >
          <Region>
            {this.backButton(parentId)}
            {this.props.displayBreadCrumbs && <LeftItem><ConnectedBreadcrumbs /></LeftItem>}
            {this.shouldShowVizViewerFavoriteButton() && this.getVizViewerFavoriteButton()}
          </Region>
        </span>

        <span className={PageHeaderStyles.RightRegionVizViewer} >
          <Region>
            {!this.props.isInServerArea && this.vizViewerPageHeaderButtons()}
            <ConnectedPageHeaderMenus buttonType="light" showFavoritesMenu={this.shouldShowFavoritesMenu()} />
          </Region>
        </span>
      </div>
    );
  }

  private getVizViewerFavoriteButton(): JSX.Element {
    return (
      <FavoriteToggleClickedTelemetryContext.Provider value={generateFavoriteToggleClickedTelemetryContext(this.props.spaceInfo)}>
        <VizViewerFavoriteButton {...this.props} />
      </FavoriteToggleClickedTelemetryContext.Provider>
    );
  }

  private shouldShowVizViewerFavoriteButton = (): boolean => {
    return !isUsageAnalyticsSubspace(this.props.spaceInfo)
      && !this.props.isOnServerStatusViz
      && !this.props.isOnSiteStatusViz
      && !isMetricSpace(this.props.spaceInfo)
      && !_.isUndefined(this.props.spaceInfo.item);
  }

  private shouldShowFavoritesMenu = (): boolean => {
    return this.shouldShowVizViewerFavoriteButton() || this.shouldShowMetricDetailFavoritesMenu();
  }

  private shouldShowMetricDetailFavoritesMenu(): boolean {
    return isMetricSpace(this.props.spaceInfo)
      && !_.isUndefined(this.props.spaceInfo.item);
  }

  private setRef = (button: ButtonWidget) => {
    if (button) {
      this.setState({ vizViewerSearchButtonRef: ReactDOM.findDOMNode(button) as HTMLElement });
    }
  }

  private setVizViewerSearchButtonFocus = () => {
    if (!_.isUndefined(this.state.vizViewerSearchButtonRef)) {
      _.defer(() => {
        this.state.vizViewerSearchButtonRef.focus();
      });
    }
  }

  private vizViewerSearchButton = (): JSX.Element => {
    return (
      <RightItem>
        {VizViewerSearchButton({
          isSearchOpen: this.state.isSearchOpen,
          onClick: this.toggleSearchVisibility,
          title: this.state.isSearchOpen ? Messages.searchClose() : Messages.searchOpen(),
          testId: this.state.isSearchOpen ? vizHeaderSearchOpenTestId : vizHeaderSearchCloseTestId,
          setRef: this.setRef
        })}
      </RightItem>
    );
  }

  private vizViewerPageHeaderButtons = (): JSX.Element => {
    if (this.state.isSearchOpen) {
      return (
        <>
          <RightItem>{PageHeaderSearch({
            headerSearchStyle: this.state.headerSearchStyle,
            searchClosed: this.onSearchClosed,
            searchClosedWithoutNeedingFocus: this.onSearchClosedWithoutNeedingFocus,
            letSearchBoxHasFocus: true,
            isSearchInputBoxActive: this.isSearchInputBoxActive
          })}</RightItem>
          {this.vizViewerSearchButton()}
        </>
      );
    } else {
      const { isInVizViewerArea, addContentFavorite, removeContentFavorite, ...vizViewerButtonsProps } = this.props;
      return (
        <>
          <VizViewerPageHeaderButtons {...vizViewerButtonsProps} />
          { isValidSpaceInfoForVizView(this.props.spaceInfo) && <div className={PageHeaderStyles.VizViewerDivider} />}
          {this.vizViewerSearchButton()}
        </>
      );
    }
  }

  private isSearchInputBoxActive = (isActive: boolean) => {
    this.setState({headerSearchStyle: isActive ? pageHeaderSearchActiveStyle : ''});
  };

  private toggleSearchVisibility = () => {

    Telemetry.sendEvent<PageHeaderButtonClickEvent>({
      type: TelemetryEvents.PageHeaderButtonClick,
      button: 'viz-viewer-search',
      action: this.state.isSearchOpen ? 'close' : 'open'
    });

    this.setState((prevState) => {
      return { isSearchOpen: !prevState.isSearchOpen };
    });
  }

  // Borrowed from TopBar.ts
  // We have to use this jquery code here because omniBox is in angular
  // so we can't use react refs here.
  private onSearchClosed = (event: BaseJQueryEventObject) => {
    if (this.state.isSearchOpen) {
      this.setState({ isSearchOpen: false }, () => {
        this.setVizViewerSearchButtonFocus();
      });
    }
  }

  private onSearchClosedWithoutNeedingFocus = (event: BaseJQueryEventObject) => {
    if (this.eventTargetOutOfSearch(event)) {
      if (this.state.isSearchOpen) {
        this.setState({ isSearchOpen: false });
      }
    }
  }

  private blurOmniboxTextBox() {
    $('input[name="omniboxTextBox"]').trigger('blur');
  }

  private eventTargetOutOfSearch(event: BaseJQueryEventObject) {
    const target = event && $(event.target);
    return !target || (target.closest('.tb-search-box-icon-button').length === 0 &&
      target.closest('.tb-omnibox-popup').length === 0);
  }

  private getVizParentWorkbookId() {
    const viewInfo: IViewDetailInfo = this.props.spaceInfo.item as IViewDetailInfo;
    const parentWorkbookId = viewInfo && viewInfo.workbook && viewInfo.workbook.id;
    return parentWorkbookId;
  }

  private getParentDatasourceID() {
    const datasourceInfo: IDataSourceDetailInfo = this.props.spaceInfo.item as IDataSourceDetailInfo;
    const parentDatasourceId = datasourceInfo && datasourceInfo.id;
    return parentDatasourceId;
  }

  private getMetricParentProjectId() {
    const metricInfo: IMetricInfo = this.props.spaceInfo.item as IMetricInfo;
    const parentProjectId = metricInfo && metricInfo.project && metricInfo.project.id;
    return parentProjectId;
  }

  private backButton = (parentId: string): JSX.Element => {
    if (!this.shouldShowBackButton(parentId)) {
      return null;
    }

    return (
      <BackButton previousUrl={this.props.previousUrl}
        parentId={parentId}
        siteUrls={this.props.siteUrls}
        serverUrls={ServerUrls.getInstance()}
        isOnDatasourceAnalyticsViz={isUsageAnalyticsSubspace(this.props.spaceInfo)}
        isOnSiteStatusViz={this.props.isOnSiteStatusViz}
        isOnServerStatusViz={this.props.isOnServerStatusViz}
        isMetricDetail={this.props.isMetricDetail}
        buttonType={'light'}
        styleOverrides={{ marginRight: '6px' }}
      />);
  }

  private shouldShowBackButton = (parentId: string): boolean => {
    return !!parentId
      || this.props.previousUrl !== PreviousUrlInitialState
      || isUsageAnalyticsSubspace(this.props.spaceInfo)
      || this.props.isOnServerStatusViz
      || this.props.isOnSiteStatusViz
      || this.props.isMetricDetail;
  }
}

export function generateFavoriteToggleClickedTelemetryContext(spaceInfo: SpaceInfo) {
  return function FavoriteToggleClickedTelemetryContext(childFunction: FavoriteToggleClickedChildFunction) {
    return (
      childFunction((toggleType: FavoriteToggleType) => {
        const eventHandler = generateTelemetryEventHandler(
          TelemetryEvents.FavoriteToggleClicked,
          spaceInfo,
          null, // array of IExplorableTypeInfo
          { id: spaceInfo.item.id, contentType: spaceInfo.item.contentType } as IContentInfo,
          ContentLocation.SpaceHeader,
          null, // content position
          null // ContentTelemetryContextSpecificData
        );
        eventHandler(toggleType);
      })
    );
  };
}
